import { NavigationType, useBlocker } from 'react-router';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import type { AppState } from '#hooks/useAppLocation.ts';

/**
 * Navigation state that lets an intentional redirect pass the blocker without a
 * prompt, e.g. after deleting the resource being edited: the unsaved edits are moot
 * once the resource is gone, and blocking the redirect would strand the user on a
 * page that can only 404. Scoping the bypass to a single tagged navigation keeps
 * every other route away (links, browser back) prompting as usual.
 *
 * The flag persists in the tagged history entry's state, so never copy a location's
 * state wholesale into a new navigation (e.g. `state: location.state`): that would
 * carry the bypass onto an unrelated navigation.
 */
export const SKIP_UNSAVED_CHANGES_PROMPT = { skipUnsavedChangesPrompt: true } as const satisfies AppState;

type Props = {
  when: boolean;
  title: React.ReactNode;
  children: React.ReactNode;
};

export function BlockNavigationDialog({ when, title, children }: Props) {
  const blocker = useBlocker(({ nextLocation, historyAction }) => {
    // History entries keep their state, so a back/forward (POP) navigation can land
    // on an entry that still carries the bypass flag; only a fresh push/replace is
    // the intentional redirect the flag was attached to
    const state = nextLocation.state as AppState;
    const bypass = historyAction !== NavigationType.Pop && Boolean(state?.skipUnsavedChangesPrompt);
    return when && !bypass;
  });

  if (blocker.state !== 'blocked') {
    // If we aren't blocking, don't render.
    return null;
  }

  return (
    <ConfirmDialog isOpen={true} title={title} onAccept={() => blocker.proceed()} onCancel={() => blocker.reset()}>
      {children}
    </ConfirmDialog>
  );
}
